import axios from "axios";

import { getLastInteraction } from "./breadcrumbs.js";
import { APP, APP_ENV, APP_RELEASE } from "./buildInfo.js";

/**
 * Rolling in-memory diagnostics for the beta error button: the last ~200
 * console entries and the last ~50 failed HTTP requests, timestamped, so a
 * report can carry "the logs before and around that time" without anything
 * being persisted or sent until the user actually clicks the button.
 *
 * Passive and best-effort like breadcrumbs: wrapping console must never
 * change console behaviour, and the axios tap must never affect a request.
 */

const MAX_CONSOLE = 200;
const MAX_FAILED = 50;

const consoleEntries = [];
const failedRequests = [];
let installed = false;

const push = (list, entry, max) => {
  list.push(entry);
  if (list.length > max) list.splice(0, list.length - max);
};

const stringify = (value) => {
  try {
    if (typeof value === "string") return value;
    if (value instanceof Error) return `${value.name}: ${value.message}`;
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

export function installDiagnosticsBuffer() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  ["log", "info", "warn", "error"].forEach((level) => {
    const original = window.console[level]?.bind(window.console);
    if (!original) return;
    window.console[level] = (...args) => {
      try {
        push(consoleEntries, {
          level,
          at: new Date().toISOString(),
          message: args.map(stringify).join(" ").slice(0, 1000),
        }, MAX_CONSOLE);
      } catch {
        // never let capture break console
      }
      original(...args);
    };
  });

  window.addEventListener("error", (event) => {
    try {
      push(consoleEntries, {
        level: "uncaught",
        at: new Date().toISOString(),
        message: String(event.message || event.error || "").slice(0, 1000),
      }, MAX_CONSOLE);
    } catch { /* ignore */ }
  });

  // Failed requests in the background — tap axios' response error path.
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        const cfg = error?.config || {};
        push(failedRequests, {
          at: new Date().toISOString(),
          method: (cfg.method || "get").toUpperCase(),
          url: String(cfg.url || "").slice(0, 300),
          status: error?.response?.status ?? null, // null = network failure
          message: String(error?.response?.data?.message || error?.message || "").slice(0, 300),
        }, MAX_FAILED);
      } catch { /* ignore */ }
      return Promise.reject(error);
    }
  );
}

/** Everything the beta report bundles, snapshotted at click time. */
export function dumpDiagnostics() {
  return {
    app: APP,
    app_env: APP_ENV,
    app_release: APP_RELEASE,
    url: typeof window !== "undefined" ? window.location.href : null,
    viewport: typeof window !== "undefined"
      ? { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio }
      : null,
    last_interaction: getLastInteraction(),
    console: [...consoleEntries],
    failed_requests: [...failedRequests],
  };
}
